import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiCheckCircle, FiHeart, FiTool } = FiIcons;

const WorkersComp = () => {
  const benefits = [
    'Medical expenses for work-related injuries',
    'Lost wage replacement benefits',
    'Disability compensation',
    'Rehabilitation services',
    'Death benefits for families',
    'Legal protection from employee lawsuits'
  ];

  const industries = [
    'Construction',
    'Manufacturing',
    'Healthcare',
    'Retail',
    'Transportation',
    'Professional Services',
    'Hospitality',
    'Technology'
  ];

  const californiaKeywords = [
    'Los Angeles', 'San Francisco', 'San Diego', 'Sacramento', 'San Jose',
    'Fresno', 'Long Beach', 'Oakland', 'Bakersfield', 'Anaheim',
    'Santa Ana', 'Riverside', 'Stockton', 'Irvine', 'Chula Vista'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-900 to-red-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              Workers Compensation Insurance for California Employers
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-red-100 mb-8 max-w-3xl mx-auto"
            >
              Required by California law for most employers. Protect your employees and your business with comprehensive workers compensation coverage through Coterie Insurance.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex justify-center"
            >
              <a
                href="https://coterie.com/quote"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors shadow-xl"
              >
                Get My Price
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Legal Requirement Section */}
      <section className="py-20 bg-yellow-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-yellow-100 border-l-4 border-yellow-500 p-6 rounded-lg">
            <div className="flex items-center">
              <SafeIcon icon={FiShield} className="h-8 w-8 text-yellow-600 mr-4" />
              <div>
                <h3 className="text-xl font-semibold text-yellow-800 mb-2">
                  California Legal Requirement
                </h3>
                <p className="text-yellow-700">
                  California law requires most employers with one or more employees to carry workers compensation insurance. Failure to comply can result in criminal charges, fines up to $100,000, and civil penalties.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Workers Compensation Benefits
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive protection for both your employees and your business.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-lg flex items-center space-x-4"
              >
                <SafeIcon icon={FiCheckCircle} className="h-6 w-6 text-red-500 flex-shrink-0" />
                <span className="text-gray-800 font-medium">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Coverage for All Industries
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Workers compensation insurance is required across all industries in California.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-red-50 p-6 rounded-xl text-center hover:bg-red-100 transition-colors"
              >
                <SafeIcon icon={FiTool} className="h-8 w-8 text-red-600 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900">{industry}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-red-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Choose Burbank Insurance + Coterie?
              </h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <SafeIcon icon={FiShield} className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">California Expertise</h3>
                    <p className="text-gray-600">Deep understanding of California workers compensation laws and requirements.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <SafeIcon icon={FiHeart} className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Employee Care</h3>
                    <p className="text-gray-600">Show your employees you care about their safety and well-being with proper coverage.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <SafeIcon icon={FiCheckCircle} className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Legal Compliance</h3>
                    <p className="text-gray-600">Stay compliant with California law and avoid costly penalties and legal issues.</p>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-white p-8 rounded-xl shadow-xl"
            >
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Get Compliant Today</h3>
                <p className="text-gray-600 mb-6">
                  Don't risk penalties or leave your employees unprotected. Get workers compensation coverage now.
                </p>
                <a
                  href="https://coterie.com/quote"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-red-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-red-700 transition-colors shadow-lg mb-4"
                >
                  Get My Price
                </a>
                <p className="text-sm text-gray-500">
                  Fast quotes • Competitive rates • California compliant
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SEO Keywords Section */}
      <section className="py-12 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-lg font-semibold text-gray-700 mb-6 text-center">
            Workers Compensation Insurance Coverage Areas in California
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 text-sm text-gray-600">
            {californiaKeywords.map((city, index) => (
              <div key={index} className="text-center p-2 bg-white rounded-lg shadow-sm">
                Workers Compensation Insurance {city}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default WorkersComp;